import { create } from "zustand";
import type { Workspace } from "../../types";
import { api, setCurrentWorkspaceId } from "@/api/client";
import { cancelComparisonPolling } from "@/stores/comparisons";

const STORAGE_KEY = "prism.currentWorkspaceId";

function loadStoredId(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return parseInt(raw) || 1;
  } catch {}
  return 1;
}

function saveStoredId(id: number) {
  try { localStorage.setItem(STORAGE_KEY, String(id)); } catch {}
}

interface WorkspacesStore {
  workspaces: Workspace[];
  currentId: number;
  current: Workspace | null;
  loading: boolean;
  error: string | null;

  fetch: () => Promise<void>;
  switchTo: (id: number) => void;
  create: (name: string, description?: string) => Promise<Workspace>;
  update: (id: number, fields: { name?: string; description?: string; primary_brand_id?: number | null }) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

// Initialize the global API workspace ID from localStorage before anything runs
const initialId = loadStoredId();
setCurrentWorkspaceId(initialId);

export const useWorkspacesStore = create<WorkspacesStore>((set, get) => ({
  workspaces: [],
  currentId: initialId,
  current: null,
  loading: false,
  error: null,

  fetch: async () => {
    set({ loading: true });
    try {
      const workspaces = await api.workspaces.list();
      const { currentId } = get();
      // Make sure current workspace still exists; fall back to default
      const current = workspaces.find(w => w.id === currentId) || workspaces[0] || null;
      const finalId = current?.id || 1;
      if (finalId !== currentId) {
        saveStoredId(finalId);
        setCurrentWorkspaceId(finalId);
      }
      set({ workspaces, current, currentId: finalId, loading: false, error: null });
    } catch (err: any) {
      set({ loading: false, error: err?.message || "Failed to load workspaces" });
    }
  },

  switchTo: (id) => {
    const { workspaces, currentId } = get();
    const current = workspaces.find(w => w.id === id) || null;
    if (!current) return;
    // Stop any in-flight comparison polling so it doesn't continue against the
    // new workspace's data with the new header.
    if (id !== currentId) cancelComparisonPolling();
    saveStoredId(id);
    setCurrentWorkspaceId(id);
    set({ currentId: id, current });
  },

  create: async (name, description) => {
    const ws = await api.workspaces.create(name, description);
    await get().fetch();
    return ws;
  },

  update: async (id, fields) => {
    await api.workspaces.update(id, fields);
    await get().fetch();
  },

  remove: async (id) => {
    await api.workspaces.delete(id);
    // If we deleted current, reset to default
    const { currentId } = get();
    if (currentId === id) {
      cancelComparisonPolling();
      saveStoredId(1);
      setCurrentWorkspaceId(1);
      set({ currentId: 1 });
    }
    await get().fetch();
  },
}));
